/**
 * @fileoverview Present supplied matter invoice and payment history while retaining each accounting source's identity. Backend case/accounting loads own records and reconciliation. Grouping and formatting here do not post invoices, acknowledge provider payment, or infer that unrelated journals settle the same obligation.
 */

import React, { useMemo } from 'react';
import './Invoices.css';
import AppIcon from "../shared/AppIcon";

const VISIBLE_XERO_ACTIVITY_SOURCE_TYPES = ['ACCPAY', 'CASHPAID', 'ACCPAYPAYMENT', 'APCREDITPAYMENT', 'ACCRECPAYMENT', 'ACCPAYCREDIT', 'MANJOURNAL'];
const VISIBLE_XERO_ACTIVITY_ACCOUNT_TYPES = ['EXPENSE'];

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});



function asNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value, currency = 'USD') {
  if (currency !== 'USD') return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(asNumber(value));
  return currencyFormatter.format(asNumber(value));
}



function formatDate(value) {
  if (!value) {
    return 'No date';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'No date';
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}



function getTimestamp(value) {
  if (!value) {
    return null;
  }
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

function uniqueJournalLines(journals) {
  const rows = new Map();
  (journals || []).forEach(row => rows.set(row.JournalLineID || row.id, row));
  return [...rows.values()];
}



function getActivityStatusLabel(journal) {
  return journal.SourceType === 'MANJOURNAL' ? 'Adjustment' : 'Recorded';
}



function getInvoiceStatus(invoice) {
  const amountDue = asNumber(invoice.AmountDue);
  const amountPaid = asNumber(invoice.AmountPaid);

  if (amountDue === 0 && amountPaid > 0) {
    return 'PAID';
  }
  if (amountDue > 0) {
    return 'UNPAID';
  }
  return 'PENDING';
}



function getInvoiceStatusTone(status) {
  switch (status) {
    case 'PAID':
      return 'success';
    case 'UNPAID':
      return 'danger';
    default:
      return 'warning';
  }
}



function getInvoiceStatusLabel(status) {
  switch (status) {
    case 'PAID':
      return 'Paid';
    case 'UNPAID':
      return 'Open';
    default:
      return 'Pending';
  }
}



function StatusPill({ tone = 'neutral', children }) {
  return (
    <span className={`xero-pill xero-pill--${tone}`}>
            {children}
        </span>);

}



function StatCard({ icon, label, value, meta, tone = 'neutral' }) {
  return (
    <div className="xero-stat-card" data-tone={tone}>
            <div className="xero-stat-icon">
                <AppIcon icon={`pi ${icon}`} aria-hidden="true" />
            </div>
            <div className="xero-stat-copy">
                <div className="xero-stat-label">{label}</div>
                <div className="xero-stat-value">{value}</div>
                <div className="xero-stat-meta">{meta}</div>
            </div>
        </div>);

}



function Invoices({ invoices = [], journals = [] }) {
  const sortedInvoices = useMemo(() => {
    return [...(invoices || [])].sort((a, b) => {
      return (getTimestamp(b.Date) || 0) - (getTimestamp(a.Date) || 0);
    });
  }, [invoices]);

  const activityEntries = useMemo(() => {
    return uniqueJournalLines(journals)
      .filter((journal) =>
        VISIBLE_XERO_ACTIVITY_SOURCE_TYPES.includes(journal.SourceType) &&
        VISIBLE_XERO_ACTIVITY_ACCOUNT_TYPES.includes(journal.AccountType) &&
        asNumber(journal.NetAmount) !== 0
      )
      .sort((a, b) => {
        return (getTimestamp(b.JournalDate) || 0) - (getTimestamp(a.JournalDate) || 0);
      });
  }, [journals]);

  const invoiceSummary = useMemo(() => {
    return sortedInvoices.reduce(

    (summary, invoice) => {
      const status = getInvoiceStatus(invoice);
      if (invoice.Type === 'ACCREC' && (!invoice.CurrencyCode || invoice.CurrencyCode === 'USD')) summary.totalDue += asNumber(invoice.AmountDue);
      if (invoice.Type === 'ACCPAY' && (!invoice.CurrencyCode || invoice.CurrencyCode === 'USD')) summary.totalPaid += asNumber(invoice.AmountDue);

      if (status === 'PAID') {
        summary.paidCount += 1;
      } else if (status === 'UNPAID') {
        summary.unpaidCount += 1;
      } else {
        summary.pendingCount += 1;
      }

      return summary;
    }, {
      totalDue: 0,
      totalPaid: 0,
      paidCount: 0,
      unpaidCount: 0,
      pendingCount: 0
    });
  }, [sortedInvoices]);

  const totalRecordedActivity = useMemo(() => {
    return activityEntries.reduce((total, journal) => total + asNumber(journal.NetAmount), 0);
  }, [activityEntries]);

  const latestActivityDate = useMemo(() => {
    const timestamps = [
      ...sortedInvoices.map((invoice) => getTimestamp(invoice.Date)),
      ...activityEntries.map((journal) => getTimestamp(journal.JournalDate))
    ].filter(Boolean);

    if (timestamps.length === 0) {
      return null;
    }

    return new Date(Math.max(...timestamps));
  }, [sortedInvoices, activityEntries]);

  const hasInvoices = sortedInvoices.length > 0;
  const hasActivity = activityEntries.length > 0;
  const hasData = hasInvoices || hasActivity;

  return (
    <div className="case-xero">
            <section className="xero-overview-panel">
                <div className="xero-overview-copy">
                    <div className="xero-overview-eyebrow">Xero</div>
                    <h2 className="xero-overview-title">Financial snapshot</h2>
                    <p className="xero-overview-description">
                        Invoices and Xero journal activity for this matter, organized so balance and activity are easy to scan.
                    </p>
                </div>

                <div className="xero-stat-grid">
                    <StatCard
            icon="pi-wallet"
            label="Recorded Activity"
            value={formatCurrency(totalRecordedActivity)}
            meta={`${activityEntries.length} ${activityEntries.length === 1 ? 'journal entry' : 'journal entries'}`}
            tone="info" />

                    <StatCard
            icon="pi-file"
            label="Invoices receivable (USD)"
            value={formatCurrency(invoiceSummary.totalDue)}
            meta={`${invoiceSummary.unpaidCount} open • ${invoiceSummary.pendingCount} pending`}
            tone="warning" />

                    <StatCard
            icon="pi-check-circle"
            label="Bills payable (USD)"
            value={formatCurrency(invoiceSummary.totalPaid)}
            meta={`${sortedInvoices.length} invoices and bills`}
            tone="success" />

                    <StatCard
            icon="pi-clock"
            label="Latest Activity"
            value={latestActivityDate ? formatDate(latestActivityDate) : 'No activity'}
            meta={latestActivityDate ? 'Most recent Xero record' : 'No invoices or journals yet'} />

                </div>
            </section>

            {!hasData &&
      <section className="xero-empty-state">
                    <div className="xero-empty-icon">
                        <AppIcon icon="pi pi-inbox" aria-hidden="true" />
                    </div>
                    <div className="xero-empty-title">No Xero data yet</div>
                    <div className="xero-empty-copy">
                        This matter does not have invoice or payment-journal records to display.
                    </div>
                </section>}


            {hasData &&
      <div className="xero-panel-grid">
                    <section className="xero-panel">
                        <div className="xero-panel-header">
                            <div>
                                <h3 className="xero-panel-title">Invoice Snapshot</h3>
                                <p className="xero-panel-description">
                                    Outstanding balance, paid amount, and current invoice status.
                                </p>
                            </div>
                            <div className="xero-panel-count">
                                {sortedInvoices.length}
                            </div>
                        </div>

                        {hasInvoices ?
          <ul className="xero-list">
                                {sortedInvoices.map(

                                (invoice) => {
              const status = getInvoiceStatus(invoice);
              const tone = getInvoiceStatusTone(status);
              const invoiceId = invoice.InvoiceID ? String(invoice.InvoiceID) : null;

              return (
                <li key={invoice.id || invoice.InvoiceID} className="xero-row">
                                            <div className="xero-row-main">
                                                <div className="xero-row-title">
                                                    <span>{invoice.Contact || 'Invoice'}</span>
                                                    {invoiceId &&
                      <span className="xero-row-id">
                                                            #{invoiceId.slice(-6)}
                                                        </span>}

                                                </div>
                                                <div className="xero-row-meta">
                                                    {invoice.Type && <span>{invoice.Type}</span>}
                                                    <span>{formatDate(invoice.Date)}</span>
                                                </div>
                                            </div>

                                            <div className="xero-row-values">
                                                <div className="xero-metric-stack">
                                                    <span className="xero-metric-label">Paid</span>
                                                    <strong className="xero-metric-value">{formatCurrency(invoice.AmountPaid, invoice.CurrencyCode || 'USD')}</strong>
                                                </div>
                                                <div className="xero-metric-stack">
                                                    <span className="xero-metric-label">Due</span>
                                                    <strong className="xero-metric-value">{formatCurrency(invoice.AmountDue, invoice.CurrencyCode || 'USD')}</strong>
                                                </div>
                                                <StatusPill tone={tone}>
                                                    {getInvoiceStatusLabel(status)}
                                                </StatusPill>
                                            </div>
                                        </li>);

            })}
                            </ul> :

          <div className="xero-panel-empty">
                                <span>No invoices found for this matter.</span>
                            </div>}

                    </section>

                    <section className="xero-panel">
                        <div className="xero-panel-header">
                            <div>
                                <h3 className="xero-panel-title">Recorded Activity</h3>
                                <p className="xero-panel-description">
                                    Expense journal activity, including reversals and corrections.
                                </p>
                            </div>
                            <div className="xero-panel-count">
                                {activityEntries.length}
                            </div>
                        </div>

                        {hasActivity ?
          <ul className="xero-list">
                                {activityEntries.map((journal) =>
            <li key={journal.id || journal.JournalLineID} className="xero-row xero-row--payment">
                                        <div className="xero-row-main">
                                            <div className="xero-row-title">
                                                <span>{journal.Description || journal.AccountName || 'Journal entry'}</span>
                                            </div>
                                            <div className="xero-row-meta">
                                                {journal.AccountName && <span>{journal.AccountName}</span>}
                                                {journal.SourceType && <span>{journal.SourceType}</span>}
                                                {journal.AccountType && <span>{journal.AccountType}</span>}
                                                <span>{formatDate(journal.JournalDate)}</span>
                                            </div>
                                        </div>

                                        <div className="xero-row-values xero-row-values--payment">
                                            <div className="xero-metric-stack">
                                                <span className="xero-metric-label">Amount</span>
                                                <strong className="xero-metric-value">{formatCurrency(journal.NetAmount)}</strong>
                                            </div>
                                            <StatusPill tone="info">{getActivityStatusLabel(journal)}</StatusPill>
                                        </div>
                                    </li>
            )}
                            </ul> :

          <div className="xero-panel-empty">
                                <span>No recorded Xero activity found for this matter.</span>
                            </div>}

                    </section>
                </div>}

        </div>);

}

export default Invoices;
